import { useEffect, useRef, useState } from "react";
import {
  Button,
  Card,
  DataState,
  Input,
  Modal,
  PageHeader,
  Select,
  useToast,
} from "@vms/ui-web";
import { useApi, useCanIDo } from "../../auth/AuthProvider";
import { useOrgContext } from "../../context/OrgContext";
import { useQuery } from "../../api/queries";

interface VenueProfile {
  id: string;
  name: string;
  slug: string;
  address?: string | null;
  websiteUrl?: string | null;
  contactEmail?: string | null;
  contactPhone?: string | null;
  logoUrl?: string | null;
  bannerUrl?: string | null;
  defaultOverlayMode: "full" | "hidden" | "banner";
  defaultBrandingTemplateId?: string | null;
  lapsedAmberDays: number;
  lapsedRedDays: number;
  timezone: string;
  bookingAutoConfirmOverride?: boolean | null;
  licencePlan: string;
  licenceFeatures?: string[] | null;
  channel: "stable" | "beta" | "dev";
  termEndsAt?: string | null;
  currentTermId?: string | null;
}

const KNOWN_LICENCE_FEATURES = [
  "cloud-leaderboards",
  "partner-api",
  "advanced-reporting",
  "multi-display",
] as const;

// Populate the timezone <select> from the browser's IANA database via
// `Intl.supportedValuesOf` — every modern Chromium/Safari/Firefox build
// ships it. Falls back to a small common-zones list on the rare older
// runtime that doesn't (avoids the dropdown rendering empty in that case).
const TIMEZONE_OPTIONS: Array<{ value: string; label: string }> = (() => {
  try {
    const zones = (Intl as unknown as { supportedValuesOf?: (k: string) => string[] })
      .supportedValuesOf?.("timeZone");
    if (zones && zones.length > 0) {
      return zones.map((z) => ({ value: z, label: z }));
    }
  } catch {
    /* fall through to static list */
  }
  return [
    "UTC",
    "Europe/London",
    "Europe/Paris",
    "Europe/Berlin",
    "Europe/Madrid",
    "America/New_York",
    "America/Chicago",
    "America/Denver",
    "America/Los_Angeles",
    "Asia/Tokyo",
    "Asia/Singapore",
    "Asia/Dubai",
    "Australia/Sydney",
  ].map((z) => ({ value: z, label: z }));
})();

/** Type-to-filter combobox over the IANA timezone list. With ~400 zones,
 *  a native `<select>` is unusable — the user wants to type "london" and
 *  see "Europe/London" surface immediately. Kept inline here because no
 *  other surface needs it yet; if a second use-case appears, lift into
 *  `@vms/ui-web`. */
function TimezoneCombobox({
  label,
  value,
  onChange,
  options,
}: {
  label: string;
  value: string;
  onChange: (next: string) => void;
  options: Array<{ value: string; label: string }>;
}) {
  const [query, setQuery] = useState(value);
  const [open, setOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(0);
  const containerRef = useRef<HTMLDivElement>(null);
  const activeRef = useRef<HTMLButtonElement>(null);

  // Keep the visible text in sync if `value` is externally replaced
  // (e.g. when the form reloads from the API).
  useEffect(() => {
    setQuery(value);
  }, [value]);

  // Click outside closes the menu and restores the canonical value so the
  // input never holds a half-typed query when the user moves on.
  useEffect(() => {
    function onDoc(e: MouseEvent) {
      if (containerRef.current && !containerRef.current.contains(e.target as Node)) {
        setOpen(false);
        setQuery(value);
      }
    }
    document.addEventListener("mousedown", onDoc);
    return () => document.removeEventListener("mousedown", onDoc);
  }, [value]);

  const q = query.trim().toLowerCase();
  const filtered =
    q.length === 0
      ? options.slice(0, 100)
      : options.filter((o) => o.label.toLowerCase().includes(q)).slice(0, 100);

  // Keep the active option scrolled into view as the user arrow-keys
  // through results.
  useEffect(() => {
    activeRef.current?.scrollIntoView({ block: "nearest" });
  }, [activeIndex, open]);

  function pick(v: string) {
    onChange(v);
    setQuery(v);
    setOpen(false);
  }

  return (
    <div ref={containerRef} className="relative">
      <Input
        label={label}
        value={query}
        onChange={(e) => {
          setQuery(e.target.value);
          setOpen(true);
          setActiveIndex(0);
        }}
        onFocus={() => setOpen(true)}
        onKeyDown={(e) => {
          if (e.key === "ArrowDown") {
            e.preventDefault();
            setOpen(true);
            setActiveIndex((i) => Math.min(i + 1, Math.max(0, filtered.length - 1)));
          } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setActiveIndex((i) => Math.max(i - 1, 0));
          } else if (e.key === "Enter") {
            if (open && filtered[activeIndex]) {
              e.preventDefault();
              pick(filtered[activeIndex].value);
            }
          } else if (e.key === "Escape") {
            setOpen(false);
            setQuery(value);
          }
        }}
        placeholder="Type to search timezones…"
        autoComplete="off"
      />
      {open && filtered.length > 0 && (
        <div className="absolute z-10 mt-1 w-full max-h-60 overflow-y-auto rounded-md border border-gray-200 bg-white shadow-lg">
          {filtered.map((o, i) => (
            <button
              key={o.value}
              ref={i === activeIndex ? activeRef : undefined}
              type="button"
              // Prevent the input's blur (which would close the menu before
              // the click registers) by suppressing the default mousedown.
              onMouseDown={(e) => e.preventDefault()}
              onClick={() => pick(o.value)}
              onMouseEnter={() => setActiveIndex(i)}
              className={`block w-full text-left px-3 py-1.5 text-sm ${
                i === activeIndex
                  ? "bg-blue-50 text-blue-900"
                  : "hover:bg-gray-50 text-gray-800"
              }`}
            >
              {o.label}
            </button>
          ))}
        </div>
      )}
      {open && filtered.length === 0 && (
        <div className="absolute z-10 mt-1 w-full rounded-md border border-gray-200 bg-white px-3 py-2 text-sm text-gray-500 shadow-lg">
          No matches
        </div>
      )}
    </div>
  );
}

function isValidWebsite(v: string): boolean {
  if (!v) return true; // empty is fine — field is optional
  try {
    const u = new URL(v);
    return u.protocol === "http:" || u.protocol === "https:";
  } catch {
    return false;
  }
}

function isValidEmail(v: string): boolean {
  if (!v) return true; // empty is fine — field is optional
  return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(v);
}

interface VenueTermRow {
  id: string;
  venueId: string;
  startsAt: string;
  endsAt: string;
  durationMonths: number;
  planAtTerm: string;
  origin: "initial" | "renewal" | "manual-edit";
  notes: string | null;
  createdAt: string;
  isCurrent: boolean;
  coversNow: boolean;
}

type AutoConfirm = "follow-org" | "force-on" | "force-off";

export function VenueSettings() {
  const { currentOrg, currentVenue, refetchVenues } = useOrgContext();
  const api = useApi();
  const canIDo = useCanIDo();
  const { addToast } = useToast();

  const profile = useQuery<VenueProfile>(
    currentVenue ? `/api/venues/${currentVenue.id}` : null,
  );

  const [form, setForm] = useState<VenueProfile | null>(null);
  const [submitting, setSubmitting] = useState(false);

  // Reissue-venueKey flow (FPZERO). Two-step: a plain reissue, and — if the
  // server reports the venue is already paired (409 hub-claimed) — a stronger
  // "force" confirmation that de-authenticates the paired hub.
  const [reissueOpen, setReissueOpen] = useState(false);
  const [needsForce, setNeedsForce] = useState(false);
  const [reissuing, setReissuing] = useState(false);
  const [reissueError, setReissueError] = useState<string | null>(null);
  const [revealKey, setRevealKey] = useState<string | null>(null);

  useEffect(() => {
    if (profile.data) setForm(profile.data);
  }, [profile.data]);

  if (!currentOrg || !currentVenue) return null;
  const canEdit = canIDo("org:settings", currentOrg.id);
  const isFpzero = canIDo("venues:create");

  const autoConfirm: AutoConfirm = !form
    ? "follow-org"
    : form.bookingAutoConfirmOverride === null || form.bookingAutoConfirmOverride === undefined
      ? "follow-org"
      : form.bookingAutoConfirmOverride
        ? "force-on"
        : "force-off";

  function setAutoConfirm(value: AutoConfirm) {
    if (!form) return;
    setForm({
      ...form,
      bookingAutoConfirmOverride:
        value === "follow-org" ? null : value === "force-on" ? true : false,
    });
  }

  const websiteInvalid =
    !!form && (form.websiteUrl ?? "").length > 0 && !isValidWebsite(form.websiteUrl ?? "");
  const emailInvalid =
    !!form && (form.contactEmail ?? "").length > 0 && !isValidEmail(form.contactEmail ?? "");

  async function save() {
    if (!form) return;
    if (websiteInvalid) {
      addToast("Website must be a valid http:// or https:// URL", "error");
      return;
    }
    if (emailInvalid) {
      addToast("Contact email must be a valid email address", "error");
      return;
    }
    setSubmitting(true);
    try {
      const payload: Record<string, unknown> = {
        name: form.name,
        address: form.address,
        websiteUrl: form.websiteUrl,
        contactEmail: form.contactEmail,
        contactPhone: form.contactPhone,
        logoUrl: form.logoUrl,
        bannerUrl: form.bannerUrl,
        defaultOverlayMode: form.defaultOverlayMode,
        lapsedAmberDays: form.lapsedAmberDays,
        lapsedRedDays: form.lapsedRedDays,
        timezone: form.timezone,
        bookingAutoConfirmOverride: form.bookingAutoConfirmOverride,
      };
      if (isFpzero) {
        payload.licencePlan = form.licencePlan;
        payload.channel = form.channel;
        payload.licenceFeatures = form.licenceFeatures ?? [];
      }
      const res = await api(`/api/venues/${form.id}`, {
        method: "PATCH",
        body: JSON.stringify(payload),
      });
      const json = await res.json();
      if (!res.ok || !json.success) {
        addToast(json.error ?? "Failed to save", "error");
        return;
      }
      addToast("Venue settings saved");
      profile.refetch();
      refetchVenues();
    } finally {
      setSubmitting(false);
    }
  }

  function closeReissue() {
    setReissueOpen(false);
    setNeedsForce(false);
    setReissueError(null);
  }

  async function doReissue(force: boolean) {
    if (!currentVenue) return;
    setReissuing(true);
    setReissueError(null);
    try {
      const res = await api(`/api/admin/venues/${currentVenue.id}/issue-venue-key`, {
        method: "POST",
        body: JSON.stringify({ force }),
      });
      const json = await res.json();
      if (res.status === 409 && json.code === "hub-claimed") {
        // Venue is paired — escalate to the force confirmation.
        setNeedsForce(true);
        return;
      }
      if (!res.ok || !json.success) {
        setReissueError(json.error ?? "Failed to reissue venueKey");
        return;
      }
      closeReissue();
      setRevealKey(json.data.venueKey as string);
      addToast("venueKey reissued");
    } finally {
      setReissuing(false);
    }
  }

  return (
    <div className="space-y-6">
      <PageHeader title="Venue settings" subtitle={currentVenue.name} />
      <DataState loading={profile.loading || !form} error={profile.error}>
        {form && (
          <fieldset disabled={!canEdit} className="space-y-6">
            <Card className="p-5 space-y-4">
              <div className="font-semibold text-gray-900">Profile</div>
              <Input
                label="Name"
                value={form.name}
                onChange={(e) => setForm({ ...form, name: e.target.value })}
              />
              <Input
                label="Address"
                value={form.address ?? ""}
                onChange={(e) => setForm({ ...form, address: e.target.value })}
              />
              <div className="grid grid-cols-2 gap-3">
                <div>
                  <Input
                    label="Contact email"
                    type="email"
                    value={form.contactEmail ?? ""}
                    onChange={(e) => setForm({ ...form, contactEmail: e.target.value })}
                    placeholder="name@example.com"
                  />
                  {emailInvalid && (
                    <p className="mt-1 text-xs text-red-600">
                      Must be a valid email address.
                    </p>
                  )}
                </div>
                <Input
                  label="Contact phone"
                  type="tel"
                  value={form.contactPhone ?? ""}
                  onChange={(e) => setForm({ ...form, contactPhone: e.target.value })}
                  placeholder="+44 7700 900000"
                />
              </div>
              <div>
                <Input
                  label="Website"
                  type="url"
                  value={form.websiteUrl ?? ""}
                  onChange={(e) => setForm({ ...form, websiteUrl: e.target.value })}
                  placeholder="https://example.com"
                />
                {websiteInvalid && (
                  <p className="mt-1 text-xs text-red-600">
                    Must be a valid http:// or https:// URL.
                  </p>
                )}
              </div>
              <TimezoneCombobox
                label="Timezone"
                value={form.timezone}
                onChange={(tz) => setForm({ ...form, timezone: tz })}
                options={TIMEZONE_OPTIONS}
              />
            </Card>

            <Card className="p-5 space-y-4">
              <div className="font-semibold text-gray-900">Display defaults</div>
              <Select
                label="Default overlay mode"
                value={form.defaultOverlayMode}
                onChange={(e) =>
                  setForm({
                    ...form,
                    defaultOverlayMode: e.target.value as "full" | "hidden" | "banner",
                  })
                }
                options={[
                  { value: "full", label: "Full — overlay screens visible" },
                  { value: "hidden", label: "Hidden — driver sees the game" },
                  { value: "banner", label: "Banner — compact top strip" },
                ]}
              />
              <div className="grid grid-cols-2 gap-3">
                <Input
                  label="Lapsed amber (days)"
                  type="number"
                  value={String(form.lapsedAmberDays)}
                  onChange={(e) =>
                    setForm({ ...form, lapsedAmberDays: Number(e.target.value) || 0 })
                  }
                />
                <Input
                  label="Lapsed red (days)"
                  type="number"
                  value={String(form.lapsedRedDays)}
                  onChange={(e) =>
                    setForm({ ...form, lapsedRedDays: Number(e.target.value) || 0 })
                  }
                />
              </div>
            </Card>

            <Card className="p-5 space-y-4">
              <div className="font-semibold text-gray-900">Bookings</div>
              <Select
                label="Auto-confirm override"
                value={autoConfirm}
                onChange={(e) => setAutoConfirm(e.target.value as AutoConfirm)}
                options={[
                  { value: "follow-org", label: "Follow organisation default" },
                  { value: "force-on", label: "Always auto-confirm at this venue" },
                  { value: "force-off", label: "Always require manual confirmation" },
                ]}
              />
            </Card>

            {isFpzero && (
              <Card className="p-5 space-y-4 border-purple-200 bg-purple-50/40">
                <div className="font-semibold text-purple-900">FPZERO controls</div>
                <div className="grid grid-cols-2 gap-3">
                  <Select
                    label="Licence plan"
                    value={form.licencePlan}
                    onChange={(e) => setForm({ ...form, licencePlan: e.target.value })}
                    options={[
                      { value: "standard", label: "Standard" },
                      { value: "premium", label: "Premium" },
                      { value: "enterprise", label: "Enterprise" },
                    ]}
                  />
                  <Select
                    label="Update channel"
                    value={form.channel}
                    onChange={(e) =>
                      setForm({
                        ...form,
                        channel: e.target.value as "stable" | "beta" | "dev",
                      })
                    }
                    options={[
                      { value: "stable", label: "Stable" },
                      { value: "beta", label: "Beta" },
                      { value: "dev", label: "Dev" },
                    ]}
                  />
                </div>
                <div>
                  <div className="text-sm font-medium text-purple-900 mb-2">
                    Licence features
                  </div>
                  <div className="flex flex-wrap gap-2">
                    {KNOWN_LICENCE_FEATURES.map((feat) => {
                      const enabled = (form.licenceFeatures ?? []).includes(feat);
                      return (
                        <button
                          key={feat}
                          type="button"
                          onClick={() => {
                            const cur = new Set(form.licenceFeatures ?? []);
                            if (enabled) cur.delete(feat);
                            else cur.add(feat);
                            setForm({ ...form, licenceFeatures: Array.from(cur) });
                          }}
                          className={`rounded-full px-3 py-1 text-xs font-medium border transition-colors ${
                            enabled
                              ? "bg-purple-600 text-white border-purple-600"
                              : "bg-white text-purple-900 border-purple-200 hover:border-purple-400"
                          }`}
                        >
                          <code>{feat}</code>
                        </button>
                      );
                    })}
                  </div>
                  <div className="mt-2 text-xs text-purple-800">
                    Takes effect on the next cert refresh. Save and click "Refresh cert" on the Licences page to apply immediately.
                  </div>
                </div>
                <LicensingTermCard
                  venueId={form.id}
                  venueName={form.name}
                  licencePlanFallback={form.licencePlan}
                />
                <div className="rounded-md border border-purple-200 bg-white p-4">
                  <div className="flex items-center justify-between gap-4">
                    <div>
                      <div className="font-semibold text-purple-900">venueKey</div>
                      <div className="text-xs text-purple-800 mt-0.5">
                        Reissue if the key was lost or cleared. The current key stops
                        working immediately and the new key is shown only once — there's
                        no way to view an existing key (it's stored hashed).
                      </div>
                    </div>
                    <Button
                      size="sm"
                      variant="secondary"
                      type="button"
                      onClick={() => {
                        setReissueOpen(true);
                        setNeedsForce(false);
                        setReissueError(null);
                      }}
                    >
                      Reissue venueKey
                    </Button>
                  </div>
                </div>
              </Card>
            )}

            <div className="flex justify-end">
              <Button onClick={save} loading={submitting} disabled={!canEdit}>
                Save changes
              </Button>
            </div>
          </fieldset>
        )}
      </DataState>

      {/* Reissue confirmation — plain first, escalates to a force warning if the
          venue is already paired (server 409 hub-claimed). */}
      <Modal isOpen={reissueOpen} onClose={closeReissue} title="Reissue venueKey">
        <div className="space-y-4">
          {!needsForce ? (
            <>
              <p className="text-sm text-gray-600">
                Mint a new venueKey for{" "}
                <span className="font-medium">{currentVenue.name}</span>? The current key
                stops working immediately, and the new key is shown{" "}
                <span className="font-medium">only once</span>.
              </p>
              {reissueError && (
                <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  {reissueError}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={closeReissue}>
                  Cancel
                </Button>
                <Button onClick={() => doReissue(false)} loading={reissuing}>
                  Reissue key
                </Button>
              </div>
            </>
          ) : (
            <>
              <div className="rounded-md bg-amber-50 border border-amber-200 px-3 py-2 text-sm text-amber-800">
                A hub is already paired with{" "}
                <span className="font-medium">{currentVenue.name}</span>. Forcing a
                reissue will <span className="font-medium">de-authenticate the paired
                hub</span> — it must be re-paired with the new key. Its cached licence
                cert may keep it running until it next reconnects.
              </div>
              {reissueError && (
                <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                  {reissueError}
                </div>
              )}
              <div className="flex justify-end gap-2">
                <Button variant="secondary" onClick={closeReissue}>
                  Cancel
                </Button>
                <Button variant="danger" onClick={() => doReissue(true)} loading={reissuing}>
                  Force reissue
                </Button>
              </div>
            </>
          )}
        </div>
      </Modal>

      <Modal isOpen={!!revealKey} onClose={() => setRevealKey(null)} title="venueKey — copy now">
        {revealKey && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              This is the only time you'll see this venueKey. The cloud only retains the
              bcrypt hash — paste it into the hub's setup wizard before closing.
            </div>
            <pre className="rounded bg-gray-50 border border-gray-200 p-3 text-xs overflow-x-auto select-all">
              {revealKey}
            </pre>
            <div className="flex justify-end">
              <Button onClick={() => setRevealKey(null)}>Done</Button>
            </div>
          </div>
        )}
      </Modal>
    </div>
  );
}

function LicensingTermCard({
  venueId,
  venueName,
  licencePlanFallback,
}: {
  venueId: string;
  venueName: string;
  licencePlanFallback: string;
}) {
  const terms = useQuery<VenueTermRow[]>(`/api/admin/venues/${venueId}/licence-terms`);
  const api = useApi();
  const { addToast } = useToast();
  const [editing, setEditing] = useState<VenueTermRow | null>(null);
  const [renewOpen, setRenewOpen] = useState(false);

  const current =
    (terms.data ?? []).find((t) => t.isCurrent) ??
    (terms.data ?? []).find((t) => t.coversNow) ??
    (terms.data ?? [])[0];

  return (
    <div className="rounded-md border border-purple-200 bg-white p-4 space-y-3">
      <div className="flex items-center justify-between">
        <div className="font-semibold text-purple-900">Licensing term</div>
        <div className="flex gap-2">
          <Button size="sm" variant="secondary" onClick={() => current && setEditing(current)} disabled={!current}>
            Edit dates
          </Button>
          <Button size="sm" onClick={() => setRenewOpen(true)}>
            Renew now
          </Button>
        </div>
      </div>
      {!current ? (
        <div className="text-sm text-gray-600">No term recorded yet.</div>
      ) : (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-sm">
          <div>
            <div className="text-xs text-gray-500">Starts</div>
            <div className="text-gray-900">{new Date(current.startsAt).toLocaleDateString()}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Ends</div>
            <div className="text-gray-900">{new Date(current.endsAt).toLocaleDateString()}</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Duration</div>
            <div className="text-gray-900">{current.durationMonths} months</div>
          </div>
          <div>
            <div className="text-xs text-gray-500">Plan at term</div>
            <div className="text-gray-900">{current.planAtTerm}</div>
          </div>
        </div>
      )}
      <TermEditModal
        term={editing}
        onClose={() => setEditing(null)}
        onSaved={() => {
          terms.refetch();
          setEditing(null);
          addToast(`${venueName}: term dates updated`);
        }}
      />
      <TermRenewModal
        open={renewOpen}
        venueId={venueId}
        venueName={venueName}
        previousEndsAt={current?.endsAt ?? null}
        planFallback={current?.planAtTerm ?? licencePlanFallback}
        onClose={() => setRenewOpen(false)}
        onRenewed={() => {
          terms.refetch();
          setRenewOpen(false);
          addToast(`${venueName}: term renewed`);
        }}
      />
    </div>
  );

  function TermEditModal({
    term,
    onClose,
    onSaved,
  }: {
    term: VenueTermRow | null;
    onClose: () => void;
    onSaved: () => void;
  }) {
    const [startsAt, setStartsAt] = useState("");
    const [endsAt, setEndsAt] = useState("");
    const [planAtTerm, setPlanAtTerm] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
      if (!term) return;
      setStartsAt(term.startsAt.slice(0, 10));
      setEndsAt(term.endsAt.slice(0, 10));
      setPlanAtTerm(term.planAtTerm);
      setErr(null);
    }, [term?.id]);

    async function save() {
      if (!term) return;
      setSubmitting(true);
      setErr(null);
      try {
        const body: Record<string, unknown> = {
          startsAt: new Date(startsAt).toISOString(),
          endsAt: new Date(endsAt).toISOString(),
        };
        if (planAtTerm && planAtTerm !== term.planAtTerm) {
          body.planAtTerm = planAtTerm;
        }
        const res = await api(`/api/admin/licence-terms/${term.id}`, {
          method: "PATCH",
          body: JSON.stringify(body),
        });
        const json = await res.json();
        if (!res.ok || !json.success) {
          setErr(json.error ?? "Failed to update term");
          return;
        }
        onSaved();
      } finally {
        setSubmitting(false);
      }
    }

    return (
      <Modal isOpen={!!term} onClose={onClose} title="Edit term dates">
        {term && (
          <div className="space-y-4">
            <div className="text-sm text-gray-600">
              Correct typos on an existing term. Plan-at-term edits change the
              billing snapshot for this period — for ongoing changes, use "Renew now".
            </div>
            <div className="grid grid-cols-2 gap-3">
              <Input
                label="Starts"
                type="date"
                value={startsAt}
                onChange={(e) => setStartsAt(e.target.value)}
              />
              <Input
                label="Ends"
                type="date"
                value={endsAt}
                onChange={(e) => setEndsAt(e.target.value)}
              />
            </div>
            <Select
              label="Plan billed for this term"
              value={planAtTerm}
              onChange={(e) => setPlanAtTerm(e.target.value)}
              options={[
                { value: "standard", label: "Standard" },
                { value: "premium", label: "Premium" },
                { value: "enterprise", label: "Enterprise" },
              ]}
            />
            {err && (
              <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
                {err}
              </div>
            )}
            <div className="flex justify-end gap-2 pt-2">
              <Button variant="secondary" onClick={onClose}>
                Cancel
              </Button>
              <Button onClick={save} loading={submitting}>
                Save dates
              </Button>
            </div>
          </div>
        )}
      </Modal>
    );
  }

  function TermRenewModal({
    open,
    venueId,
    venueName,
    previousEndsAt,
    planFallback,
    onClose,
    onRenewed,
  }: {
    open: boolean;
    venueId: string;
    venueName: string;
    previousEndsAt: string | null;
    planFallback: string;
    onClose: () => void;
    onRenewed: () => void;
  }) {
    const [startsAt, setStartsAt] = useState("");
    const [duration, setDuration] = useState(12);
    const [endsAt, setEndsAt] = useState("");
    const [endsTouched, setEndsTouched] = useState(false);
    const [planAtTerm, setPlanAtTerm] = useState(planFallback);
    const [notes, setNotes] = useState("");
    const [submitting, setSubmitting] = useState(false);
    const [err, setErr] = useState<string | null>(null);

    useEffect(() => {
      if (!open) return;
      const s = previousEndsAt
        ? previousEndsAt.slice(0, 10)
        : new Date().toISOString().slice(0, 10);
      setStartsAt(s);
      setEndsAt(addMonthsIso(s, 12));
      setDuration(12);
      setEndsTouched(false);
      setPlanAtTerm(planFallback);
      setNotes("");
      setErr(null);
    }, [open, previousEndsAt, planFallback]);

    async function submit() {
      setSubmitting(true);
      setErr(null);
      try {
        const res = await api(`/api/admin/venues/${venueId}/licence-terms/renew`, {
          method: "POST",
          body: JSON.stringify({
            startsAt: new Date(startsAt).toISOString(),
            endsAt: endsTouched ? new Date(endsAt).toISOString() : undefined,
            durationMonths: duration,
            planAtTerm,
            notes: notes.trim() || undefined,
          }),
        });
        const json = await res.json();
        if (!res.ok || !json.success) {
          setErr(json.error ?? "Failed to renew");
          return;
        }
        onRenewed();
      } finally {
        setSubmitting(false);
      }
    }

    return (
      <Modal isOpen={open} onClose={onClose} title={`Renew term — ${venueName}`}>
        <div className="space-y-4">
          <div className="grid grid-cols-3 gap-3">
            <Input
              label="Starts"
              type="date"
              value={startsAt}
              onChange={(e) => {
                const next = e.target.value;
                setStartsAt(next);
                if (!endsTouched) setEndsAt(addMonthsIso(next, duration));
              }}
            />
            <Input
              label="Duration (months)"
              type="number"
              value={String(duration)}
              onChange={(e) => {
                const n = Math.max(1, Math.min(120, Number(e.target.value) || 12));
                setDuration(n);
                if (!endsTouched) setEndsAt(addMonthsIso(startsAt, n));
              }}
            />
            <Input
              label="Ends"
              type="date"
              value={endsAt}
              onChange={(e) => {
                setEndsTouched(true);
                setEndsAt(e.target.value);
              }}
            />
          </div>
          <Select
            label="Plan billed for this term"
            value={planAtTerm}
            onChange={(e) => setPlanAtTerm(e.target.value)}
            options={[
              { value: "standard", label: "Standard" },
              { value: "premium", label: "Premium" },
              { value: "enterprise", label: "Enterprise" },
            ]}
          />
          <Input
            label="Notes (optional)"
            value={notes}
            onChange={(e) => setNotes(e.target.value)}
            placeholder="PO #1234, manual renewal etc."
          />
          {err && (
            <div className="rounded-md bg-red-50 border border-red-200 px-3 py-2 text-sm text-red-700">
              {err}
            </div>
          )}
          <div className="flex justify-end gap-2 pt-2">
            <Button variant="secondary" onClick={onClose}>
              Cancel
            </Button>
            <Button onClick={submit} loading={submitting}>
              Renew term
            </Button>
          </div>
        </div>
      </Modal>
    );
  }
}

function addMonthsIso(iso: string, months: number): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return "";
  d.setUTCMonth(d.getUTCMonth() + months);
  return d.toISOString().slice(0, 10);
}
